import { BLOCKS, TORCH } from "../world/blocks";
import {
  armorInfo,
  ARROW,
  BONE,
  BOW,
  CHARCOAL,
  COAL,
  COOKED_BEEF,
  COOKED_MUTTON,
  COOKED_PORKCHOP,
  DIAMOND,
  FEATHER,
  FLINT,
  GOLD_INGOT,
  GUNPOWDER,
  SPIDER_EYE,
  IRON_INGOT,
  isBlockItem,
  LEATHER,
  RAW_BEEF,
  RAW_MUTTON,
  RAW_PORKCHOP,
  STICK,
  STRING,
  toolInfo,
  type ArmorSlot,
  type ArmorTier,
} from "../world/items";

/**
 * All HUD artwork is painted from the same procedural atlas the world uses, so
 * the UI can never drift out of sync with the in-world textures.
 */

let tileCanvases: (HTMLCanvasElement | null)[] | null = null;

/** Registered by the game once the atlas exists, so icons can reuse it. */
export function provideAtlasTiles(tiles: (HTMLCanvasElement | null)[]): void {
  tileCanvases = tiles;
}

function getTileCanvas(tile: number): HTMLCanvasElement | null {
  return tileCanvases?.[tile] ?? null;
}

/**
 * Draw an isometric block icon from the same procedural atlas the world uses,
 * so the hotbar can never drift out of sync with the in-world textures.
 * Uses true isometric projection with affine texture mapping and directional shading.
 */
export function drawBlockIcon(canvas: HTMLCanvasElement, blockId: number): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const def = BLOCKS[blockId];
  if (!def || def.top < 0) return;

  const topTile = getTileCanvas(def.top);
  const sideTile = getTileCanvas(def.side);
  if (!topTile || !sideTile) return;

  const size = canvas.width;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, size, size);

  // Isometric cube geometry:
  const cx = size / 2;
  const hw = Math.round(size * 0.40);
  const hh = Math.round(hw * 0.50);
  const sideH = Math.round(size * 0.40);
  const topY = Math.round((size - (2 * hh + sideH)) / 2);

  // 1. Top face (100% brightness)
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(cx, topY);
  ctx.lineTo(cx + hw, topY + hh);
  ctx.lineTo(cx, topY + 2 * hh);
  ctx.lineTo(cx - hw, topY + hh);
  ctx.closePath();
  ctx.clip();
  ctx.transform(hw / 16, hh / 16, -hw / 16, hh / 16, cx, topY);
  ctx.drawImage(topTile, 0, 0, 16, 16);
  ctx.restore();

  // 2. Left face (80% brightness)
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(cx - hw, topY + hh);
  ctx.lineTo(cx, topY + 2 * hh);
  ctx.lineTo(cx, topY + 2 * hh + sideH);
  ctx.lineTo(cx - hw, topY + hh + sideH);
  ctx.closePath();
  ctx.clip();
  ctx.transform(hw / 16, hh / 16, 0, sideH / 16, cx - hw, topY + hh);
  ctx.drawImage(sideTile, 0, 0, 16, 16);
  ctx.fillStyle = "rgba(0, 0, 0, 0.20)";
  ctx.fill();
  ctx.restore();

  // 3. Right face (60% brightness)
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(cx, topY + 2 * hh);
  ctx.lineTo(cx + hw, topY + hh);
  ctx.lineTo(cx + hw, topY + hh + sideH);
  ctx.lineTo(cx, topY + 2 * hh + sideH);
  ctx.closePath();
  ctx.clip();
  ctx.transform(hw / 16, -hh / 16, 0, sideH / 16, cx, topY + 2 * hh);
  ctx.drawImage(sideTile, 0, 0, 16, 16);
  ctx.fillStyle = "rgba(0, 0, 0, 0.40)";
  ctx.fill();
  ctx.restore();
}

/**
 * Draw any item: block items reuse the isometric atlas icon, while non-block
 * items use flat procedural sprites. Unknown ids clear the canvas.
 */
export function drawItemIcon(canvas: HTMLCanvasElement, id: number): void {
  if (id === TORCH) {
    const itemSprite = ITEM_SPRITES[id];
    if (itemSprite) {
      renderPixelGrid16(canvas, itemSprite.rows, itemSprite.palette);
      return;
    }
  }
  if (isBlockItem(id)) {
    drawBlockIcon(canvas, id);
    return;
  }
  const itemSprite = ITEM_SPRITES[id];
  if (itemSprite) {
    renderPixelGrid16(canvas, itemSprite.rows, itemSprite.palette);
    return;
  }
  const tool = toolInfo(id);
  if (tool && tool.kind !== "bow") {
    drawToolIcon(canvas, tool.kind, tool.tier);
    return;
  }
  const armor = armorInfo(id);
  if (armor) {
    drawArmorItemIcon(canvas, armor.slot, armor.tier);
    return;
  }
  clearBlockIcon(canvas);
}

/**
 * Extract the raw 16x16 pixel colors for an item ID.
 * Returns an array of 16 rows, each having 16 hex color strings or null.
 */
export function getItemPixelGrid(id: number): (string | null)[][] | null {
  if (id === TORCH) {
    const s = ITEM_SPRITES[id];
    if (s) return parseSpriteGrid(s.rows, s.palette);
  }

  if (isBlockItem(id)) return null;

  const tool = toolInfo(id);
  if (tool && tool.kind !== "bow") {
    const palette = TOOL_PALETTES[tool.tier] ?? TOOL_PALETTES[1];
    const sprite =
      tool.kind === "sword"
        ? SWORD_SPRITE
        : tool.kind === "pickaxe"
          ? PICKAXE_SPRITE
          : tool.kind === "axe"
            ? AXE_SPRITE
            : SHOVEL_SPRITE;
    return parseToolGrid(sprite, palette);
  }

  const armor = armorInfo(id);
  if (armor) {
    const palette = ARMOR_PALETTES[armor.tier];
    const sprite =
      armor.slot === "helmet"
        ? HELMET_SPRITE
        : armor.slot === "chestplate"
          ? CHESTPLATE_SPRITE
          : armor.slot === "leggings"
            ? LEGGINGS_SPRITE
            : BOOTS_SPRITE;
    return parseSpriteGrid(sprite, palette);
  }

  const s = ITEM_SPRITES[id];
  if (s) return parseSpriteGrid(s.rows, s.palette);

  return null;
}

const BOW_PULLING_SPRITES = [
  {
    rows: [
      ".0..............",
      ".12........3333.",
      "..45....33356654",
      "...45.335654444.",
      "....4576444...8.",
      "....3457.....8..",
      "...37945.....8..",
      "...367.45...8...",
      "..354...45.8....",
      "..364....458....",
      "..354.....8.....",
      ".354....88......",
      ".364...8........",
      ".364.88.........",
      ".3548...........",
      "..4.............",
    ],
    palette: {
      "0": "#ffffff",
      "1": "#b1b1b1",
      "2": "#d8d8d8",
      "3": "#493615",
      "4": "#281e0b",
      "5": "#896727",
      "6": "#684e1e",
      "7": "#6b6b6b",
      "8": "#444444",
      "9": "#969696",
    },
  },
  {
    rows: [
      "................",
      "..0.........111.",
      "..23....11114546",
      "...64.114544666.",
      "....64756666..8.",
      "....1647......8.",
      "...17964.....8..",
      "...157.64....8..",
      "..146...64..8...",
      "..156....64.8...",
      "..146.....64....",
      "..146.....8.....",
      ".146....88......",
      ".156..88........",
      ".14688..........",
      "..6.............",
    ],
    palette: {
      "0": "#ffffff",
      "1": "#493615",
      "2": "#b1b1b1",
      "3": "#d8d8d8",
      "4": "#896727",
      "5": "#684e1e",
      "6": "#281e0b",
      "7": "#6b6b6b",
      "8": "#444444",
      "9": "#969696",
    },
  },
  {
    rows: [
      "................",
      "................",
      "...0....1111111.",
      "...23.1145444546",
      "....64756666666.",
      "....1647......8.",
      "...17964......8.",
      "...157.64....8..",
      "..146...64...8..",
      "..156....64..8..",
      "..146.....64.8..",
      "..146......64...",
      "..146......8....",
      "..156..8888.....",
      "..14688.........",
      "...6............",
    ],
    palette: {
      "0": "#ffffff",
      "1": "#493615",
      "2": "#b1b1b1",
      "3": "#d8d8d8",
      "4": "#896727",
      "5": "#684e1e",
      "6": "#281e0b",
      "7": "#6b6b6b",
      "8": "#444444",
      "9": "#969696",
    },
  },
] as const;

/**
 * Extract the raw 16x16 pixel colors for a bow pulling stage (0, 1, or 2).
 */
export function getBowPullingPixelGrid(stage: 0 | 1 | 2): (string | null)[][] {
  const sprite = BOW_PULLING_SPRITES[stage];
  return parseSpriteGrid(sprite.rows, sprite.palette);
}

function parseSpriteGrid(
  rows: readonly string[],
  palette: Readonly<Record<string, string>>,
): (string | null)[][] {
  const grid: (string | null)[][] = [];
  for (let y = 0; y < 16; y++) {
    const row = rows[y] ?? "";
    const rowArr: (string | null)[] = [];
    for (let x = 0; x < 16; x++) {
      const ch = row[x] ?? ".";
      if (ch === ".") {
        rowArr.push(null);
      } else {
        rowArr.push(palette[ch] ?? null);
      }
    }
    grid.push(rowArr);
  }
  return grid;
}

function parseToolGrid(
  sprite: readonly string[],
  palette: ToolPalette,
): (string | null)[][] {
  const grid: (string | null)[][] = [];
  for (let y = 0; y < 16; y++) {
    const row = sprite[y] ?? "";
    const rowArr: (string | null)[] = [];
    for (let x = 0; x < 16; x++) {
      const ch = row[x] ?? ".";
      if (ch === ".") {
        rowArr.push(null);
      } else {
        const color =
          STICK_PALETTE[ch] ??
          (ch === "O"
            ? palette.O
            : ch === "S"
              ? palette.S
              : ch === "M"
                ? palette.M
                : ch === "B"
                  ? palette.B
                  : ch === "L"
                    ? palette.L
                    : ch === "G"
                      ? palette.G
                      : null);
        rowArr.push(color ?? null);
      }
    }
    grid.push(rowArr);
  }
  return grid;
}

function renderPixelGrid16(
  canvas: HTMLCanvasElement,
  rows: readonly string[],
  palette: Readonly<Record<string, string>>,
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const size = canvas.width;
  const scale = size / 16;
  ctx.clearRect(0, 0, size, size);

  for (let y = 0; y < 16; y++) {
    const row = rows[y];
    if (!row) continue;
    const py = Math.round(y * scale);
    const ph = Math.round((y + 1) * scale) - py;

    for (let x = 0; x < 16; x++) {
      const ch = row[x];
      if (ch === ".") continue;
      const color = palette[ch];
      if (!color) continue;

      const px = Math.round(x * scale);
      const pw = Math.round((x + 1) * scale) - px;
      ctx.fillStyle = color;
      ctx.fillRect(px, py, pw, ph);
    }
  }
}

interface ItemSpriteDef {
  readonly rows: readonly string[];
  readonly palette: Readonly<Record<string, string>>;
}

const ITEM_SPRITES: Readonly<Record<number, ItemSpriteDef>> = {
  [TORCH]: {
    rows: [
      "................",
      "................",
      "................",
      "................",
      "................",
      "................",
      ".......01.......",
      ".......23.......",
      ".......45.......",
      ".......67.......",
      ".......48.......",
      ".......47.......",
      ".......67.......",
      ".......59.......",
      ".......4A.......",
      ".......5B.......",
    ],
    palette: {
      "0": "#FFD800",
      "1": "#FF8F00",
      "2": "#FFFF97",
      "3": "#FFFFFF",
      "4": "#9F7F50",
      "5": "#6D5736",
      "6": "#957546",
      "7": "#55452E",
      "8": "#423522",
      "9": "#3D301D",
      "A": "#3B2E1B",
      "B": "#372A17",
    },
  },
  [STICK]: {
    rows: [
      "................",
      "................",
      ".............01.",
      "............021.",
      "...........021..",
      "..........031...",
      ".........021....",
      "........031.....",
      ".......021......",
      "......031.......",
      ".....021........",
      "....031.........",
      "...021..........",
      "..021...........",
      "..11............",
      "................",
    ],
    palette: {
      "0": "#493615",
      "1": "#281E0B",
      "2": "#896727",
      "3": "#684E1E",
    },
  },
  [COAL]: {
    rows: [
      "................",
      "................",
      ".....0000.......",
      "....012210......",
      "...03452330.....",
      "...043212130....",
      "...0663454230...",
      "..031363441327..",
      "..015231113637..",
      "..0311133316667.",
      "..0333631638387.",
      "..0863666668687.",
      "...78863866887..",
      "....788887777...",
      ".....7777.......",
      "................",
    ],
    palette: {
      "0": "#1C1C1E",
      "1": "#2E2E2E",
      "2": "#323232",
      "3": "#252525",
      "4": "#363636",
      "5": "#393E46",
      "6": "#261E24",
      "7": "#101015",
      "8": "#1F1721",
    },
  },
  [CHARCOAL]: {
    rows: [
      "................",
      ".......000......",
      ".....012314.....",
      "....43567614....",
      "...4326762214...",
      "...42037527284..",
      "...410032723284.",
      "..4108001138004.",
      "..4182000000814.",
      "..4811180081214.",
      "..4081618008184.",
      "..4018118100804.",
      "...40188100004..",
      "....400004444...",
      ".....4444.......",
      "................",
    ],
    palette: {
      "0": "#1D1A14",
      "1": "#2B261D",
      "2": "#423B2F",
      "3": "#312B22",
      "4": "#13110D",
      "5": "#7D6F58",
      "6": "#4E4536",
      "7": "#605543",
      "8": "#231F18",
    },
  },
  [IRON_INGOT]: {
    rows: [
      "................",
      "................",
      "..........00....",
      ".......000112...",
      "....0001333312..",
      ".00013333333312.",
      "0433333333334432",
      "0143333334443215",
      "0114334443622615",
      "0111443622221115",
      "061136222211655.",
      ".061362266555...",
      "..06167555......",
      "...0055.........",
      "................",
      "................",
    ],
    palette: {
      "0": "#5E5E5E",
      "1": "#A8A8A8",
      "2": "#727272",
      "3": "#D8D8D8",
      "4": "#FFFFFF",
      "5": "#353535",
      "6": "#828282",
      "7": "#585858",
    },
  },
  [GOLD_INGOT]: {
    rows: [
      "................",
      "................",
      "..........00....",
      ".......000110...",
      "....0002222220..",
      ".00012222222213.",
      "0422222222224423",
      "0142222224442513",
      "0124224442655513",
      "0121742655556113",
      "062126555561133.",
      ".061265566333...",
      "..06166333......",
      "...0033.........",
      "................",
      "................",
    ],
    palette: {
      "0": "#B26411",
      "1": "#FAD64A",
      "2": "#FDF55F",
      "3": "#752802",
      "4": "#FFFDE0",
      "5": "#DC9613",
      "6": "#E9B115",
      "7": "#FFFFFF",
    },
  },
  [DIAMOND]: {
    rows: [
      "................",
      "................",
      "......0000......",
      ".....011120.....",
      "....01334256....",
      "...0134424356...",
      "...0131212336...",
      "..013133447576..",
      "..013234448576..",
      "..011544438776..",
      "..023377775586..",
      "...6375555756...",
      "...6975555746...",
      "....68334456....",
      ".....666666.....",
      "................",
    ],
    palette: {
      "0": "#11727A",
      "1": "#FFFFFF",
      "2": "#D5FFF6",
      "3": "#4AEDD9",
      "4": "#A1FBE8",
      "5": "#20C5B5",
      "6": "#145E53",
      "7": "#1AAAA7",
      "8": "#1C919A",
      "9": "#2CE0D8",
    },
  },
  [RAW_BEEF]: {
    rows: [
      "................",
      "................",
      "................",
      ".........0000...",
      "........012340..",
      ".......01534230.",
      "......025553236.",
      "....00355235107.",
      "...035532351367.",
      "...052345523677.",
      "..031355243667..",
      "..01552443667...",
      "..7011430067....",
      "...70000677.....",
      "....77777.......",
      "................",
    ],
    palette: {
      "0": "#AD1D17",
      "1": "#EA8873",
      "2": "#E2625A",
      "3": "#E03E35",
      "4": "#E27269",
      "5": "#E2534A",
      "6": "#7B1713",
      "7": "#470A06",
    },
  },
  [COOKED_BEEF]: {
    rows: [
      "................",
      "................",
      "................",
      ".........0000...",
      "........012130..",
      ".......04536160.",
      "......033423417.",
      "....00645342147.",
      "...042364531687.",
      "...01453345687..",
      "..012545314887..",
      "..05125412687...",
      "...053326887....",
      "...77488877.....",
      ".....7777.......",
      "................",
    ],
    palette: {
      "0": "#4E2719",
      "1": "#713F2D",
      "2": "#7C4835",
      "3": "#8E523C",
      "4": "#522F1F",
      "5": "#985C43",
      "6": "#673728",
      "7": "#2A160D",
      "8": "#3F2116",
    },
  },
  [RAW_PORKCHOP]: {
    rows: [
      "................",
      "................",
      ".........0000...",
      "........011123..",
      ".......01124403.",
      "......054112513.",
      "....00214224523.",
      "...044112224516.",
      "...025211255536.",
      "..011451257513..",
      "..011257575136..",
      "..01227775136...",
      "...305774366....",
      "...3305236......",
      ".....6666.......",
      "................",
    ],
    palette: {
      "0": "#A75353",
      "1": "#EF7070",
      "2": "#FF7777",
      "3": "#853E3E",
      "4": "#FF8C8C",
      "5": "#FFADAD",
      "6": "#512626",
      "7": "#FFC6C6",
    },
  },
  [COOKED_PORKCHOP]: {
    rows: [
      "................",
      "................",
      "................",
      ".........0110...",
      "........023214..",
      ".......02567514.",
      "......036683714.",
      ".....0257366814.",
      "....02768673514.",
      "...12537668354..",
      "..123866385114..",
      "..15578685194...",
      "...123751094....",
      "...0111104......",
      ".....4444.......",
      "................",
    ],
    palette: {
      "0": "#8C6932",
      "1": "#997942",
      "2": "#BCA474",
      "3": "#DACBA4",
      "4": "#5F4F27",
      "5": "#C5AD77",
      "6": "#D3C088",
      "7": "#E2D3AC",
      "8": "#CFBA81",
      "9": "#81602B",
    },
  },
  [RAW_MUTTON]: {
    rows: [
      "................",
      "...........00...",
      "..........1230..",
      "..........1240..",
      ".........12540..",
      ".........15637..",
      "........168637..",
      ".......1684507..",
      ".....11265850...",
      "....166286220...",
      "...1624442637...",
      "..16844448607...",
      "..1628448240....",
      "..0325665307....",
      "..700000077.....",
      "...777777.......",
    ],
    palette: {
      "0": "#96211B",
      "1": "#AD332E",
      "2": "#E2625A",
      "3": "#BC2922",
      "4": "#D12E26",
      "5": "#E27269",
      "6": "#E88A82",
      "7": "#470A06",
      "8": "#DD3830",
    },
  },
  [COOKED_MUTTON]: {
    rows: [
      "................",
      "...........00...",
      "..........1230..",
      "..........1240..",
      ".........12540..",
      ".........15637..",
      "........168637..",
      ".......1684507..",
      ".....11265850...",
      "....166286220...",
      "...1624442637...",
      "..16844448607...",
      "..1628448240....",
      "..0325665307....",
      "..700000077.....",
      "...777777.......",
    ],
    palette: {
      "0": "#522F1F",
      "1": "#6B3A2A",
      "2": "#9D6147",
      "3": "#7C402F",
      "4": "#82483A",
      "5": "#9C6854",
      "6": "#A3705A",
      "7": "#2A160D",
      "8": "#884F40",
    },
  },
  [BOW]: {
    rows: [
      "................",
      "...........0000.",
      "........00012213",
      "......001213333.",
      ".....042333..5..",
      "....0464....5...",
      "...0464....5....",
      "...024....5.....",
      "..013....5......",
      "..023...5.......",
      "..013..5........",
      ".013..5.........",
      ".023.5..........",
      ".0235...........",
      ".013............",
      "..3.............",
    ],
    palette: {
      "0": "#493615",
      "1": "#896727",
      "2": "#684E1E",
      "3": "#281E0B",
      "4": "#6B6B6B",
      "5": "#444444",
      "6": "#969696",
    },
  },
  [ARROW]: {
    rows: [
      "................",
      "................",
      "............012.",
      "..........03302.",
      "..........2432..",
      "..........4502..",
      ".........45.2...",
      "........45......",
      ".......45.......",
      "......45........",
      ".....45.........",
      "...675..........",
      "..6768..........",
      "..868...........",
      "...8............",
      "................",
    ],
    palette: {
      "0": "#969696",
      "1": "#FFFFFF",
      "2": "#444444",
      "3": "#D8D8D8",
      "4": "#896727",
      "5": "#281E0B",
      "6": "#E0E0E0",
      "7": "#C6C6C6",
      "8": "#3F3F3F",
    },
  },
  [STRING]: {
    rows: [
      "................",
      "..........0.....",
      ".........121....",
      ".........0.2....",
      ".........210....",
      "....1.....221...",
      "...02.......20..",
      "..12..0101...21.",
      "..0..1222201..0.",
      "..21.20...220.1.",
      "...20122...12.0.",
      "....22010102.12.",
      "......22222.02..",
      "........21012...",
      ".........222....",
      "................",
    ],
    palette: {
      "0": "#DBDBDB",
      "1": "#F7F7F7",
      "2": "#2F4547",
    },
  },
  [FEATHER]: {
    rows: [
      ".............01.",
      "............0230",
      "...........0230.",
      "..........02340.",
      ".........02340..",
      "........02340...",
      ".......02340....",
      "......02340.....",
      ".....02340......",
      "....02340.......",
      "...02340........",
      "..02340.........",
      ".02340..........",
      ".0540...........",
      "..00............",
      "................",
    ],
    palette: {
      "0": "#606060",
      "1": "#B0B0B0",
      "2": "#D8D8D8",
      "3": "#FFFFFF",
      "4": "#989898",
      "5": "#6D5334",
    },
  },
  [FLINT]: {
    rows: [
      "................",
      ".......00.......",
      "......0120......",
      ".....013220.....",
      "....01343220....",
      "...0134443220...",
      "...0134443220...",
      "...0113432220...",
      "....01122220....",
      "....0112220.....",
      ".....01220......",
      "......020.......",
      ".......0........",
      "................",
      "................",
      "................",
    ],
    palette: {
      "0": "#151515",
      "1": "#3A3A3C",
      "2": "#28282A",
      "3": "#545458",
      "4": "#75757A",
    },
  },
  [BONE]: {
    rows: [
      "................",
      "...........010..",
      "..........01233.",
      "..........024213",
      "..........022103",
      ".........022333.",
      "........0223....",
      ".......0223.....",
      "......0223......",
      "....00223.......",
      "..114423........",
      ".124443.........",
      ".010413.........",
      "..03103.........",
      "....33..........",
      "................",
    ],
    palette: {
      "0": "#CBC6A5",
      "1": "#E8E5D2",
      "2": "#FCFBED",
      "3": "#7B7E6B",
      "4": "#FFFFFD",
    },
  },
  [LEATHER]: {
    rows: [
      "................",
      "......0000......",
      "..00.012210.00..",
      "..010222212013..",
      "...0221222213...",
      "...0121221213...",
      "....02122213....",
      "....01222213....",
      "....02212223....",
      "....02212223....",
      "...0112121113...",
      "...0222222213...",
      "...0211111213...",
      "..011331133113..",
      "..033..33..333..",
      "................",
    ],
    palette: {
      "0": "#542716",
      "1": "#9E492A",
      "2": "#C65C35",
      "3": "#3D1C10",
    },
  },
  [GUNPOWDER]: {
    rows: [
      "................",
      "................",
      "................",
      ".......0000.....",
      ".....00122100...",
      "....0123223210..",
      "...012332233210.",
      "..0122333332210.",
      "..0133223223310.",
      "..012332233210..",
      "...01223332210..",
      "....012222210...",
      ".....00111100...",
      ".......0000.....",
      "................",
      "................",
    ],
    palette: {
      "0": "#2C2C2C",
      "1": "#484848",
      "2": "#6B6B6B",
      "3": "#8C8C8C",
    },
  },
  [SPIDER_EYE]: {
    rows: [
      "................",
      "................",
      ".......0000.....",
      ".....00122100...",
      "....0123443210..",
      "...012455554210.",
      "..01245566554210",
      "..01355677655310",
      "..01355677655310",
      "..01245566554210",
      "...012455554210.",
      "....0123443210..",
      ".....00122100...",
      ".......0000.....",
      "................",
      "................",
    ],
    palette: {
      "0": "#34000B",
      "1": "#590918",
      "2": "#7F1326",
      "3": "#9E1C33",
      "4": "#BA243E",
      "5": "#D7324E",
      "6": "#3A000A",
      "7": "#FFFFFF",
    },
  },
};


interface ToolPalette {
  readonly O: string;
  readonly S: string;
  readonly M: string;
  readonly B: string;
  readonly L: string;
  readonly G: string;
}

const TOOL_PALETTES: Readonly<Record<number, ToolPalette>> = {
  1: { O: "#20180A", S: "#372910", M: "#594319", B: "#6B511F", L: "#755821", G: "#886626" }, // Wood
  2: { O: "#181818", S: "#494949", M: "#6C6C6C", B: "#7F7F7F", L: "#898989", G: "#9A9A9A" }, // Stone
  3: { O: "#181818", S: "#444444", M: "#969696", B: "#C1C1C1", L: "#D8D8D8", G: "#FFFFFF" }, // Iron
  4: { O: "#082520", S: "#0E3F36", M: "#1E8A77", B: "#27B29A", L: "#2BC7AC", G: "#33EBCB" }, // Diamond
};

const STICK_PALETTE: Readonly<Record<string, string>> = {
  "1": "#281E0B",
  "2": "#493615",
  "3": "#684E1E",
  "4": "#896727",
};

const SWORD_SPRITE = [
  ".............SSS",
  "............SGLS",
  "...........SGLGS",
  "..........SGLGS.",
  ".........SGLGS..",
  "........SGLGS...",
  "..OO...SGLGS....",
  "..OMO.SGLGS.....",
  "...OBOGLGS......",
  "...OBOLGS.......",
  "....OBOO........",
  "...23OBMO.......",
  "..241.OOMO......",
  "OO31....OO......",
  "OBO.............",
  "OOO.............",
] as const;

const PICKAXE_SPRITE = [
  "................",
  "................",
  "......SSSSS.....",
  ".....SGLBBLS23..",
  "......SOOOBB41..",
  "..........2LBO..",
  ".........231BLO.",
  "........241.OBO.",
  ".......231..OBO.",
  "......241...OLO.",
  ".....231....OGO.",
  "....241......O..",
  "...231..........",
  "..241...........",
  "..11............",
  "................",
] as const;

const AXE_SPRITE = [
  "................",
  ".........SS.....",
  "........SGGS....",
  ".......SGBLS....",
  "......SGBBB23...",
  "......OGLBMB1...",
  ".......OO2BMBO..",
  "........231BBO..",
  ".......241.OO...",
  "......231.......",
  ".....231........",
  "....241.........",
  "...231..........",
  "..241...........",
  "..11............",
  "................",
] as const;

const SHOVEL_SPRITE = [
  "................",
  "................",
  "...........SSO..",
  "..........SGGBO.",
  ".........SGLBGO.",
  "........SGLBLGO.",
  ".........2BLGO..",
  "........241GO...",
  ".......241.O....",
  "......231.......",
  ".....241........",
  "....231.........",
  "..2241..........",
  "..241...........",
  "...11...........",
  "................",
] as const;

/** Flat tool sprites: authentic 16x16 pixel-grid sword, pickaxe, axe and shovel sprites. */
function drawToolIcon(
  canvas: HTMLCanvasElement,
  kind: "pickaxe" | "axe" | "shovel" | "sword",
  tier: number,
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const size = canvas.width;
  const scale = size / 16;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, size, size);

  const palette = TOOL_PALETTES[tier] ?? TOOL_PALETTES[1];
  const sprite =
    kind === "sword"
      ? SWORD_SPRITE
      : kind === "pickaxe"
        ? PICKAXE_SPRITE
        : kind === "axe"
          ? AXE_SPRITE
          : SHOVEL_SPRITE;

  for (let y = 0; y < 16; y++) {
    const row = sprite[y];
    const py = Math.round(y * scale);
    const ph = Math.round((y + 1) * scale) - py;

    for (let x = 0; x < 16; x++) {
      const ch = row[x];
      if (ch === ".") continue;

      const color =
        STICK_PALETTE[ch] ??
        (ch === "O"
          ? palette.O
          : ch === "S"
            ? palette.S
            : ch === "M"
              ? palette.M
              : ch === "B"
                ? palette.B
                : ch === "L"
                  ? palette.L
                  : ch === "G"
                    ? palette.G
                    : null);

      if (!color) continue;

      const px = Math.round(x * scale);
      const pw = Math.round((x + 1) * scale) - px;
      ctx.fillStyle = color;
      ctx.fillRect(px, py, pw, ph);
    }
  }
}



const HELMET_SPRITE = [
  "................",
  "................",
  "................",
  ".....000000.....",
  "....01111123....",
  "...0144111223...",
  "...0141112223...",
  "...0110003223...",
  "...0105555323...",
  "...0105555323...",
  "...0135555323...",
  "....03....33....",
  "................",
  "................",
  "................",
  "................",
] as const;

const CHESTPLATE_SPRITE = [
  "................",
  "................",
  "....00....00....",
  "..00120..01103..",
  ".011123..011123.",
  ".01111100111123.",
  ".02144111111223.",
  "..014111111223..",
  "..321111111223..",
  "...0111111123...",
  "...3111111223...",
  "...3211112223...",
  "...3222222223...",
  "....32222223....",
  ".....333333.....",
  "................",
] as const;

const LEGGINGS_SPRITE = [
  "................",
  "................",
  "....00000000....",
  "...0111111123...",
  "...0144111123...",
  "...0142222123...",
  "...0122332123...",
  "...0123..0123...",
  "...0123..0123...",
  "...0123..0123...",
  "...0123..0123...",
  "...0123..0123...",
  "...0123..0123...",
  "...0223..0223...",
  "...0333..0333...",
  "................",
] as const;

const BOOTS_SPRITE = [
  "................",
  "................",
  "................",
  "....000..000....",
  "...0123..0123...",
  "...0423..0423...",
  "...0123..0123...",
  "...0123..0123...",
  "...0123..0123...",
  "..04123..01110..",
  ".011223..021123.",
  ".02223....32223.",
  ".0333......3333.",
  "................",
  "................",
  "................",
] as const;

const ARMOR_PALETTES: Readonly<Record<ArmorTier, Readonly<Record<string, string>>>> = {
  leather: {
    "0": "#342014",
    "1": "#945D3B",
    "2": "#72482D",
    "3": "#21140D",
    "4": "#B87A4F",
    "5": "#070403",
  },
  iron: {
    "0": "#2D2D2D",
    "1": "#C6C6C6",
    "2": "#969696",
    "3": "#191919",
    "4": "#FFFFFF",
    "5": "#1E0E08",
  },
  diamond: {
    "0": "#2D2D2D",
    "1": "#33EBCB",
    "2": "#1E8A77",
    "3": "#191919",
    "4": "#FFFFFF",
    "5": "#1E0E08",
  },
};

function drawArmorItemIcon(
  canvas: HTMLCanvasElement,
  slot: ArmorSlot,
  tier: ArmorTier,
): void {
  const sprite =
    slot === "helmet"
      ? HELMET_SPRITE
      : slot === "chestplate"
        ? CHESTPLATE_SPRITE
        : slot === "leggings"
          ? LEGGINGS_SPRITE
          : BOOTS_SPRITE;
  const palette = ARMOR_PALETTES[tier];
  renderPixelGrid16(canvas, sprite, palette);
}

const EMPTY_HELMET_SPRITE = [
  "................",
  "................",
  "................",
  ".....######.....",
  "....#......#....",
  "...#........#...",
  "...#........#...",
  "...#........#...",
  "...#.##..##.#...",
  "...#........#...",
  "....#......#....",
  "....#......#....",
  ".....#....#.....",
  "......####......",
  "................",
  "................",
] as const;

const EMPTY_CHESTPLATE_SPRITE = [
  "................",
  "................",
  "....########....",
  "..##........##..",
  ".#............#.",
  ".#............#.",
  ".#............#.",
  "..#..........#..",
  "..#..........#..",
  "...#........#...",
  "...#........#...",
  "...#........#...",
  "...#........#...",
  "....#......#....",
  ".....######.....",
  "................",
] as const;

const EMPTY_LEGGINGS_SPRITE = [
  "................",
  "................",
  "....########....",
  "...#........#...",
  "...#........#...",
  "...#........#...",
  "...#...##...#...",
  "...#..#..#..#...",
  "...#..#..#..#...",
  "...#..#..#..#...",
  "...#..#..#..#...",
  "...#..#..#..#...",
  "...#..#..#..#...",
  "...#..#..#..#...",
  "...####..####...",
  "................",
] as const;

const EMPTY_BOOTS_SPRITE = [
  "................",
  "................",
  "................",
  "....###..###....",
  "...#..#..#..#...",
  "...#..#..#..#...",
  "...#..#..#..#...",
  "...#..#..#..#...",
  "...#..#..#..#...",
  "..#...#..#...#..",
  ".#....#..#....#.",
  ".#...#....#...#.",
  ".####......####.",
  "................",
  "................",
  "................",
] as const;

export function drawEmptyArmorSlotIcon(
  canvas: HTMLCanvasElement,
  slot: ArmorSlot,
): void {
  const sprite =
    slot === "helmet"
      ? EMPTY_HELMET_SPRITE
      : slot === "chestplate"
        ? EMPTY_CHESTPLATE_SPRITE
        : slot === "leggings"
          ? EMPTY_LEGGINGS_SPRITE
          : EMPTY_BOOTS_SPRITE;
  const size = canvas.width;
  const scale = size / 16;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, size, size);

  ctx.fillStyle = "rgba(255, 255, 255, 0.22)";
  for (let y = 0; y < 16; y++) {
    const row = sprite[y];
    const py = Math.round(y * scale);
    const ph = Math.round((y + 1) * scale) - py;
    for (let x = 0; x < 16; x++) {
      if (row[x] !== "#") continue;
      const px = Math.round(x * scale);
      const pw = Math.round((x + 1) * scale) - px;
      ctx.fillRect(px, py, pw, ph);
    }
  }
}

/** Wipe an icon canvas — used when a slot becomes empty again. */
export function clearBlockIcon(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext("2d");
  if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
}

// --- pixel sprites ---------------------------------------------------------

/** 9x8 heart silhouette, one character per pixel. */
const HEART_ROWS = [
  "011001100",
  "111111110",
  "111111111",
  "111111111",
  "011111110",
  "001111100",
  "000111000",
  "000010000",
] as const;

/** 8x8 air bubble silhouette. */
const BUBBLE_ROWS = [
  "00111100",
  "01111110",
  "11111111",
  "11111111",
  "11111111",
  "11111111",
  "01111110",
  "00111100",
] as const;

const FILLED_HEART = [220, 46, 46] as const;
const EMPTY_HEART = [58, 22, 24] as const;
const FILLED_BUBBLE = [200, 232, 255] as const;
const EMPTY_BUBBLE = [48, 72, 96] as const;

function paintSprite(
  canvas: HTMLCanvasElement,
  rows: readonly string[],
  fill: readonly [number, number, number],
  empty: readonly [number, number, number],
  /** Fraction of the sprite that is "filled", measured from the left. */
  fraction: number,
  scale: number,
): void {
  const w = rows[0].length;
  const h = rows.length;
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (rows[y][x] !== "1") continue;
      const on = x + 0.5 <= fraction * w;
      const c = on ? fill : empty;
      ctx.fillStyle = `rgb(${c[0]},${c[1]},${c[2]})`;
      ctx.fillRect(x * scale, y * scale, scale, scale);
    }
  }
}

/** Draw a heart in one of three states: 0 = empty, 0.5 = half, 1 = full. */
export function drawHeart(canvas: HTMLCanvasElement, state: 0 | 0.5 | 1): void {
  paintSprite(canvas, HEART_ROWS, FILLED_HEART, EMPTY_HEART, state, 2);
}

/** Draw an air bubble: 1 = full, 0 = popped. */
export function drawBubble(canvas: HTMLCanvasElement, filled: boolean): void {
  paintSprite(canvas, BUBBLE_ROWS, FILLED_BUBBLE, EMPTY_BUBBLE, filled ? 1 : 0, 2);
}

// Authentic Minecraft 1.8.8 HUD armor icons (from gui/icons.png)
const ARMOR_ICON_EMPTY = [
  ".00...00.",
  "0110.0110",
  "011101110",
  ".0111110.",
  ".0111110.",
  ".0111110.",
  ".0111110.",
  ".0111110.",
  "..00000..",
] as const;

const ARMOR_ICON_HALF = [
  ".00...00.",
  "0430.0110",
  "033301110",
  "023301110",
  ".0330110.",
  ".0330110.",
  ".0330110.",
  ".0220110.",
  "..00000..",
] as const;

const ARMOR_ICON_FULL = [
  ".00...00.",
  "0430.0430",
  "033303330",
  "023333320",
  ".0333330.",
  ".0333330.",
  ".0333330.",
  ".0222220.",
  "..00000..",
] as const;

const ARMOR_ICON_PALETTE: Readonly<Record<string, string>> = {
  "0": "#000000",
  "1": "#3D3D3D",
  "2": "#696A70",
  "3": "#B8B9C4",
  "4": "#E6E7F2",
};

/** Draw an armor chestplate icon in one of three states: 0 = empty, 0.5 = half, 1 = full. */
export function drawArmorIcon(canvas: HTMLCanvasElement, state: 0 | 0.5 | 1): void {
  const sprite =
    state === 1
      ? ARMOR_ICON_FULL
      : state === 0.5
        ? ARMOR_ICON_HALF
        : ARMOR_ICON_EMPTY;
  const scale = 2;
  canvas.width = 9 * scale;
  canvas.height = 9 * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  for (let y = 0; y < 9; y++) {
    const row = sprite[y];
    for (let x = 0; x < 9; x++) {
      const ch = row[x];
      if (ch === ".") continue;
      const color = ARMOR_ICON_PALETTE[ch];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(x * scale, y * scale, scale, scale);
    }
  }
}

// Authentic Minecraft 1.8.8 HUD hunger / drumstick icons (from gui/icons.png at x: 16, 52, 61, y: 27)
const FOOD_ICON_EMPTY = [
  "..00.....",
  ".0110....",
  "011110...",
  "0111110..",
  ".011110..",
  "..01110..",
  "...000100",
  "......010",
  "......00.",
] as const;

const FOOD_ICON_HALF = [
  "..00.....",
  ".0120....",
  "013420...",
  "0115460..",
  ".012270..",
  "..01890..",
  "...000A00",
  "......0B0",
  "......00.",
] as const;

const FOOD_ICON_FULL = [
  "..00.....",
  ".0120....",
  "013140...",
  "0214540..",
  ".067550..",
  "..06770..",
  "...000800",
  "......090",
  "......00.",
] as const;

const FOOD_PALETTE_EMPTY: Readonly<Record<string, string>> = {
  "0": "#000000",
  "1": "#282828",
};

const FOOD_PALETTE_HALF: Readonly<Record<string, string>> = {
  "0": "#000000",
  "1": "#282828",
  "2": "#B21818",
  "3": "#E8C4A4",
  "4": "#D42A2A",
  "5": "#D7977A",
  "6": "#B88458",
  "7": "#9D6D43",
  "8": "#613C1B",
  "9": "#7B512D",
  "A": "#E2D5AA",
  "B": "#FFF7DC",
};

const FOOD_PALETTE_FULL: Readonly<Record<string, string>> = {
  "0": "#000000",
  "1": "#D42A2A",
  "2": "#B21818",
  "3": "#DFB18F",
  "4": "#B88458",
  "5": "#9D6D43",
  "6": "#613C1B",
  "7": "#7B512D",
  "8": "#E2D5AA",
  "9": "#FFF7DC",
};

/** Draw a hunger drumstick / shank icon in one of three states: 0 = empty, 0.5 = half, 1 = full. */
export function drawFood(canvas: HTMLCanvasElement, state: 0 | 0.5 | 1): void {
  const sprite =
    state === 1
      ? FOOD_ICON_FULL
      : state === 0.5
        ? FOOD_ICON_HALF
        : FOOD_ICON_EMPTY;
  const palette =
    state === 1
      ? FOOD_PALETTE_FULL
      : state === 0.5
        ? FOOD_PALETTE_HALF
        : FOOD_PALETTE_EMPTY;

  const scale = 2;
  canvas.width = 9 * scale;
  canvas.height = 9 * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  for (let y = 0; y < 9; y++) {
    const row = sprite[y];
    for (let x = 0; x < 9; x++) {
      const ch = row[x];
      if (ch === ".") continue;
      const color = palette[ch];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(x * scale, y * scale, scale, scale);
    }
  }
}
